import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Table, Button, Tag, Space, Modal, Form,
  Input, InputNumber, Select, Statistic, Popconfirm, message,
  Drawer, Tooltip, Divider,
} from 'antd';
import {
  AppstoreOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  HomeOutlined, WarningOutlined, ReloadOutlined,
  SwapOutlined, HistoryOutlined, ArrowUpOutlined, ArrowDownOutlined, AimOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axios';
import { hssColors, commonStyles } from '../config/theme';
import Footer from '../components/Footer';

const { Option } = Select;

const CATEGORIAS = [
  { valor: 'cables',       label: 'Cables',       color: 'blue'    },
  { valor: 'arneses',      label: 'Arneses',       color: 'cyan'    },
  { valor: 'antenas',      label: 'Antenas',       color: 'green'   },
  { valor: 'tornilleria',  label: 'Tornillería',   color: 'default' },
  { valor: 'herramientas', label: 'Herramientas',  color: 'orange'  },
  { valor: 'consumibles',  label: 'Consumibles',   color: 'purple'  },
  { valor: 'electronica',  label: 'Electrónica',   color: 'red'     },
  { valor: 'otros',        label: 'Otros',         color: 'default' },
];
const catColor = (c) => CATEGORIAS.find(x => x.valor === c)?.color || 'default';
const catLabel = (c) => CATEGORIAS.find(x => x.valor === c)?.label || c;

const MOV_CONFIG = {
  entrada: { label: 'Entrada',  color: 'success', icon: <ArrowUpOutlined />,   cantLabel: 'Unidades a agregar' },
  salida:  { label: 'Salida',   color: 'error',   icon: <ArrowDownOutlined />, cantLabel: 'Unidades a retirar' },
  ajuste:  { label: 'Ajuste',   color: 'warning', icon: <AimOutlined />,       cantLabel: 'Nueva cantidad total' },
};

const MOTIVOS = {
  entrada: ['Compra', 'Devolución de técnico', 'Transferencia entre depósitos', 'Donación', 'Otro'],
  salida:  ['Orden de trabajo', 'Pérdida / extravío', 'Uso interno', 'Devolución a proveedor', 'Baja por daño', 'Otro'],
  ajuste:  ['Conteo físico', 'Corrección de error de carga', 'Otro'],
};

const fmtFecha = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-PY', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const Accesorios = () => {
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);

  // Modal editar/crear ítem
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editando, setEditando]           = useState(null);
  const [editForm]                        = Form.useForm();

  // Modal registrar movimiento
  const [movModalOpen, setMovModalOpen] = useState(false);
  const [movItem, setMovItem]           = useState(null);
  const [movForm]                       = Form.useForm();
  const movTipo = Form.useWatch('tipo', movForm);

  // Drawer historial
  const [histOpen, setHistOpen]   = useState(false);
  const [histItem, setHistItem]   = useState(null);
  const [histData, setHistData]   = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/accesorios');
      setItems(r.data);
    } catch {
      message.error('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Crear / Editar ítem ──────────────────────────────────────────────────────
  const abrirNuevo = () => {
    setEditando(null);
    editForm.resetFields();
    editForm.setFieldsValue({ cantidad: 0, unidad: 'unidades' });
    setEditModalOpen(true);
  };

  const abrirEditar = (item) => {
    setEditando(item);
    editForm.setFieldsValue(item);
    setEditModalOpen(true);
  };

  const guardarItem = async () => {
    try {
      const values = await editForm.validateFields();
      if (editando) {
        // cantidad NO se envía al PUT — solo metadatos
        const { cantidad, ...meta } = values;
        await axiosInstance.put(`/accesorios/${editando.id}`, meta);
        message.success('Ítem actualizado');
      } else {
        await axiosInstance.post('/accesorios', values);
        message.success('Ítem creado');
      }
      setEditModalOpen(false);
      cargar();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.error || 'Error al guardar');
    }
  };

  const eliminar = async (id) => {
    try {
      await axiosInstance.delete(`/accesorios/${id}`);
      message.success('Eliminado');
      cargar();
    } catch {
      message.error('Error al eliminar');
    }
  };

  // ── Movimientos ──────────────────────────────────────────────────────────────
  const abrirMovimiento = (item) => {
    setMovItem(item);
    movForm.resetFields();
    movForm.setFieldsValue({ tipo: 'salida', cantidad: 1 });
    setMovModalOpen(true);
  };

  const guardarMovimiento = async () => {
    try {
      const values = await movForm.validateFields();
      await axiosInstance.post(`/accesorios/${movItem.id}/movimiento`, values);
      message.success('Movimiento registrado');
      setMovModalOpen(false);
      cargar();
      // Refresh historial si está abierto para este ítem
      if (histOpen && histItem?.id === movItem.id) {
        cargarHistorial(movItem);
      }
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.error || 'Error al registrar movimiento');
    }
  };

  // ── Historial ────────────────────────────────────────────────────────────────
  const cargarHistorial = useCallback(async (item) => {
    setHistLoading(true);
    try {
      const r = await axiosInstance.get(`/accesorios/${item.id}/movimientos`);
      setHistData(r.data);
    } catch {
      message.error('Error al cargar historial');
    } finally {
      setHistLoading(false);
    }
  }, []);

  const abrirHistorial = (item) => {
    setHistItem(item);
    setHistOpen(true);
    cargarHistorial(item);
  };

  // ── Columnas tabla ───────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Nombre', dataIndex: 'nombre', key: 'nombre',
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
      render: (n, record) => (
        <span
          style={{ fontWeight: 600, color: hssColors.primary, cursor: 'pointer' }}
          onClick={() => navigate(`/accesorios/${record.id}`)}
          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
        >
          {n}
        </span>
      ),
    },
    {
      title: 'Categoría', dataIndex: 'categoria', key: 'categoria', width: 140,
      render: c => <Tag color={catColor(c)}>{catLabel(c)}</Tag>,
      filters: CATEGORIAS.map(c => ({ text: c.label, value: c.valor })),
      onFilter: (val, r) => r.categoria === val,
    },
    {
      title: 'Stock', key: 'stock', width: 200,
      sorter: (a, b) => (a.cantidad || 0) - (b.cantidad || 0),
      render: (_, record) => {
        const qty = record.cantidad ?? 0;
        const min = record.stock_minimo;
      const color = qty === 0 ? hssColors.danger : (min != null && qty <= min) ? hssColors.warning : hssColors.success;
        return (
          <Space size={8}>
            <span style={{ fontWeight: 700, fontSize: 16, color, minWidth: 28, display: 'inline-block' }}>
              {qty}
            </span>
            <span style={{ fontSize: 12, color: hssColors.textSecondary }}>{record.unidad}</span>
            <Divider type="vertical" style={{ margin: 0 }} />
            <Tooltip title="Registrar movimiento (entrada / salida / ajuste)">
              <Button size="small" icon={<SwapOutlined />} onClick={() => abrirMovimiento(record)} />
            </Tooltip>
            <Tooltip title="Ver historial de movimientos">
              <Button size="small" icon={<HistoryOutlined />} onClick={() => abrirHistorial(record)} />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Precio unit.', dataIndex: 'precio_unitario', key: 'precio', width: 120,
      render: p => p || <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: 'Ubicación', dataIndex: 'ubicacion', key: 'ubicacion',
      render: u => u || <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: 'Descripción', dataIndex: 'descripcion', key: 'desc', ellipsis: true,
      render: d => d || <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: '', key: 'acciones', width: 80, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => abrirEditar(record)} />
          <Popconfirm title="¿Eliminar este ítem y su historial?" onConfirm={() => eliminar(record.id)} okText="Sí" cancelText="No">
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const columnsHist = [
    {
      title: 'Fecha', dataIndex: 'fecha', key: 'fecha', width: 140,
      render: f => <span style={{ fontSize: 12 }}>{fmtFecha(f)}</span>,
    },
    {
      title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 100,
      render: t => {
        const cfg = MOV_CONFIG[t] || {};
        return <Tag icon={cfg.icon} color={cfg.color}>{cfg.label || t}</Tag>;
      },
    },
    {
      title: 'Cant.', dataIndex: 'cantidad', key: 'cant', width: 70,
      render: (q, r) => {
        const sign = r.tipo === 'salida' ? '-' : r.tipo === 'entrada' ? '+' : '→';
        const color = r.tipo === 'salida' ? hssColors.danger : r.tipo === 'entrada' ? hssColors.success : hssColors.warning;
        return <span style={{ fontWeight: 700, color }}>{sign}{q}</span>;
      },
    },
    {
      title: 'Saldo', key: 'saldo', width: 100,
      render: (_, r) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {r.saldo_anterior} → <strong>{r.saldo_nuevo}</strong>
        </span>
      ),
    },
    { title: 'Motivo', dataIndex: 'motivo', key: 'motivo', render: m => m || '—' },
    { title: 'Referencia', dataIndex: 'referencia', key: 'ref', width: 120, render: r => r || '—' },
    {
      title: 'Usuario', dataIndex: 'usuario_nombre', key: 'usuario', width: 120,
      render: u => <span style={{ color: hssColors.textSecondary }}>{u || '—'}</span>,
    },
  ];

  const totalSKUs  = items.length;
  const stockBajo  = items.filter(i => i.stock_minimo != null && (i.cantidad || 0) > 0 && (i.cantidad || 0) <= i.stock_minimo).length;
  const sinStock   = items.filter(i => (i.cantidad || 0) === 0).length;
  const cats       = [...new Set(items.map(i => i.categoria))].length;

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <AppstoreOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Inventario de Accesorios</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>Stock con historial de movimientos</p>
            </div>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={cargar} loading={loading} style={{ borderRadius: 8 }}>Actualizar</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={abrirNuevo} style={{ borderRadius: 8 }}>Nuevo ítem</Button>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
          </Space>
        </div>
      </div>

      <div style={{ padding: '24px 32px', flex: 1 }}>
        {/* Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {[
            { title: 'Total SKUs',        value: totalSKUs, color: hssColors.primary },
            { title: 'Categorías',         value: cats,      color: hssColors.info },
            { title: 'Bajo mínimo',        value: stockBajo, color: hssColors.warning },
            { title: 'Sin stock',          value: sinStock,  color: hssColors.danger },
          ].map(s => (
            <Col xs={12} lg={6} key={s.title}>
              <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, borderLeft: `4px solid ${s.color}` }}>
                <Statistic
                  title={<span style={{ color: hssColors.textSecondary, fontSize: 13 }}>{s.title}</span>}
                  value={s.value}
                  valueStyle={{ color: s.color, fontWeight: 700 }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Alerta stock bajo */}
        {(stockBajo > 0 || sinStock > 0) && (
          <Card style={{ marginBottom: 16, borderRadius: 10, border: 'none', borderLeft: `4px solid ${hssColors.warning}`, boxShadow: commonStyles.shadowSm }}>
            <Space wrap>
              <WarningOutlined style={{ color: hssColors.warning }} />
              {sinStock > 0 && <span style={{ color: hssColors.danger, fontWeight: 600 }}>{sinStock} ítem{sinStock > 1 ? 's' : ''} sin stock.</span>}
              {stockBajo > 0 && <span style={{ color: hssColors.warning, fontWeight: 600 }}>{stockBajo} bajo el mínimo configurado.</span>}
              <span style={{ color: hssColors.textSecondary }}>
                {items.filter(i => (i.cantidad || 0) === 0 || (i.stock_minimo != null && (i.cantidad || 0) <= i.stock_minimo)).map(i => i.nombre).join(' · ')}
              </span>
            </Space>
          </Card>
        )}

        {/* Tabla */}
        <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }} styles={{ body: { padding: 0 } }}>
          <Table
            columns={columns}
            dataSource={items}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 'max-content' }}
            style={{ borderRadius: 12, overflow: 'hidden' }}
            rowClassName={r => (r.cantidad || 0) === 0 ? 'row-sin-stock' : (r.stock_minimo != null && (r.cantidad || 0) <= r.stock_minimo) ? 'row-stock-bajo' : ''}
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `${t} ítems` }}
          />
        </Card>
      </div>

      <style>{`
        .row-sin-stock td { background-color: #fff1f0 !important; }
        .row-stock-bajo td { background-color: #fffbe6 !important; }
      `}</style>

      {/* ── Modal crear / editar ítem ──────────────────────────────────────────── */}
      <Modal
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={guardarItem}
        title={editando ? 'Editar ítem' : 'Nuevo ítem de inventario'}
        okText={editando ? 'Guardar cambios' : 'Crear'}
        cancelText="Cancelar"
        width={480}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="nombre" label="Nombre" rules={[{ required: true, message: 'Requerido' }]}>
            <Input placeholder="Ej: Cable RCA blanco 1m" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="categoria" label="Categoría" rules={[{ required: true, message: 'Requerido' }]}>
                <Select placeholder="Seleccionar">
                  {CATEGORIAS.map(c => <Option key={c.valor} value={c.valor}><Tag color={c.color}>{c.label}</Tag></Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="unidad" label="Unidad">
                <Select>
                  {['unidades', 'metros', 'juegos', 'piezas', 'rollos', 'litros', 'bolsas'].map(u =>
                    <Option key={u} value={u}>{u}</Option>
                  )}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {!editando && (
            <Form.Item name="cantidad" label="Cantidad inicial" help="Después de crear, los cambios de stock se registran como movimientos.">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          )}
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="precio_unitario" label="Precio unitario">
                <Input placeholder="Ej: 15.000 Gs" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ubicacion" label="Ubicación">
                <Input placeholder="Ej: Estante A3" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="stock_minimo" label="Stock mínimo" help="Recibirás alertas cuando el stock llegue a este nivel.">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Ej: 5" />
          </Form.Item>
          <Form.Item name="descripcion" label="Descripción">
            <Input.TextArea rows={2} placeholder="Notas opcionales..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Modal registrar movimiento ─────────────────────────────────────────── */}
      <Modal
        open={movModalOpen}
        onCancel={() => setMovModalOpen(false)}
        onOk={guardarMovimiento}
        title={
          <Space>
            <SwapOutlined style={{ color: hssColors.primary }} />
            <span>Registrar movimiento</span>
            {movItem && <Tag style={{ fontWeight: 600, fontFamily: 'monospace' }}>{movItem.nombre}</Tag>}
          </Space>
        }
        okText="Registrar"
        cancelText="Cancelar"
        width={440}
        destroyOnClose
      >
        {movItem && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: hssColors.gray50, borderRadius: 8, border: `1px solid ${hssColors.gray200}` }}>
            <span style={{ color: hssColors.textSecondary, fontSize: 13 }}>Stock actual: </span>
            <span style={{
              fontWeight: 700, fontSize: 18,
              color: (movItem.cantidad || 0) === 0 ? hssColors.danger : (movItem.cantidad || 0) < 5 ? hssColors.warning : hssColors.success,
            }}>
              {movItem.cantidad ?? 0}
            </span>
            <span style={{ color: hssColors.textSecondary, fontSize: 13 }}> {movItem.unidad}</span>
          </div>
        )}

        <Form form={movForm} layout="vertical">
          <Form.Item name="tipo" label="Tipo de movimiento" rules={[{ required: true }]}>
            <Select size="large">
              <Option value="entrada">
                <Space><ArrowUpOutlined style={{ color: hssColors.success }} /><strong>Entrada</strong> — agregar unidades</Space>
              </Option>
              <Option value="salida">
                <Space><ArrowDownOutlined style={{ color: hssColors.danger }} /><strong>Salida</strong> — retirar unidades</Space>
              </Option>
              <Option value="ajuste">
                <Space><AimOutlined style={{ color: hssColors.warning }} /><strong>Ajuste</strong> — establecer cantidad exacta</Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="cantidad"
            label={MOV_CONFIG[movTipo]?.cantLabel || 'Cantidad'}
            rules={[{ required: true, message: 'Ingrese una cantidad' }]}
            help={movTipo === 'ajuste' ? 'El stock quedará en este valor exacto (no es un delta).' : undefined}
          >
            <InputNumber min={1} size="large" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="motivo" label="Motivo" rules={[{ required: true, message: 'Seleccione un motivo' }]}>
            <Select placeholder="Seleccionar motivo" showSearch>
              {(MOTIVOS[movTipo] || MOTIVOS.entrada).map(m => <Option key={m} value={m}>{m}</Option>)}
            </Select>
          </Form.Item>

          <Form.Item name="referencia" label="Referencia (opcional)">
            <Input placeholder="Ej: Orden #45, Factura 1234..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Drawer historial de movimientos ───────────────────────────────────── */}
      <Drawer
        open={histOpen}
        onClose={() => setHistOpen(false)}
        title={
          <Space>
            <HistoryOutlined style={{ color: hssColors.primary }} />
            <span>Historial: {histItem?.nombre}</span>
            {histItem && (
              <Tag style={{
                fontWeight: 700,
                color: (histItem.cantidad || 0) === 0 ? hssColors.danger : hssColors.success,
              }}>
                Stock actual: {histItem.cantidad ?? 0} {histItem.unidad}
              </Tag>
            )}
          </Space>
        }
        width={820}
        extra={
          histItem && (
            <Button icon={<SwapOutlined />} type="primary" onClick={() => { setHistOpen(false); abrirMovimiento(histItem); }}>
              Registrar movimiento
            </Button>
          )
        }
      >
        <Table
          columns={columnsHist}
          dataSource={histData}
          rowKey="id"
          loading={histLoading}
          size="small"
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 20, showTotal: t => `${t} movimientos` }}
          locale={{ emptyText: 'Sin movimientos registrados aún' }}
        />
      </Drawer>

      <Footer />
    </div>
  );
};

export default Accesorios;
